import React from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import Loading from 'Loading'
import { Alert, CTA, Button } from 'goustouicomponents'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { browserHistory } from 'react-router'

import routes from 'config/routes'
import logger from 'utils/logger'

import OrdersList from './OrdersList'

import accountCss from '../Account/Account.css'
import css from './MyDeliveries.css'

class MyDeliveries extends React.PureComponent {
  componentDidMount() {
    this.fetchOrdersAndAddresses()
  }

  getErrorMessage() {
    const { didErrorFetchingPendingOrders, didErrorFetchingProjectedOrders } = this.props

    const errorMessages = {
      pendingOrderError:
        "We're not able to display your upcoming orders right now. Please try again later.",
      projectedOrderError:
        "We're not able to display your future deliveries right now. Please try again later.",
      genericError: "We're not able to display your deliveries right now. Please try again later.",
    }

    if (didErrorFetchingProjectedOrders !== null) {
      return errorMessages.projectedOrderError
    } else if (didErrorFetchingPendingOrders !== null) {
      return errorMessages.pendingOrderError
    } else {
      return errorMessages.genericError
    }
  }

  fetchOrdersAndAddresses = async () => {
    const { userId, userLoadData, userLoadNewOrders } = this.props

    try {
      if (!userId) {
        await userLoadData()
      }

      userLoadNewOrders(true)
    } catch (error) {
      logger.error(error)
    }
  }

  retryFetch = () => {
    this.fetchOrdersAndAddresses()
  }

  static renderFetchError = (retryFetch, errorMessage) => (
    <div>
      <Alert type="danger">
        <p>{errorMessage}</p>
      </Alert>
      <div className={css.button}>
        <Button onClick={retryFetch}>Retry</Button>
      </div>
    </div>
  )

  handleOrderAnotherBoxClick = () => {
    datadogRum.addAction('order_another_box_my_deliveries_button_clicked')
    browserHistory.push(routes.client.deliveryDetails)
  }

  static renderLoading = (
    <div className={css.spinner}>
      <Loading />
    </div>
  )

  renderOrders() {
    const {
      didErrorFetchingPendingOrders,
      didErrorFetchingProjectedOrders,
      isFetchingOrders,
      userId,
    } = this.props
    if (isFetchingOrders || !userId) {
      return MyDeliveries.renderLoading
    }

    if (didErrorFetchingPendingOrders !== null || didErrorFetchingProjectedOrders !== null) {
      const errorMessage = this.getErrorMessage()
      const error = didErrorFetchingPendingOrders

      datadogLogs.logger.error('MyDeliveries: orders fetching failed', {
        userId,
        error,
        renderedErrorMessage: errorMessage,
      })

      return MyDeliveries.renderFetchError(this.retryFetch, errorMessage)
    }

    return <OrdersList />
  }

  render() {
    return (
      <div className={accountCss.accountContainer} data-testid="myDeliveries">
        <Helmet title="Gousto Deliveries | Manage All Upcoming Deliveries" />
        <div className={css.titleContainer}>
          <h1 className={css.title}>Deliveries</h1>
          <div className={css.subTitleContainer}>
            <p className={css.subTitle}>
              Here you can manage all of your upcoming deliveries, select recipes, skip or cancel
              orders as you need.
            </p>
            <CTA
              onClick={this.handleOrderAnotherBoxClick}
              variant="secondary"
              size="small"
              testingSelector="addBoxButton"
            >
              Order another box
            </CTA>
          </div>
        </div>
        {this.renderOrders()}
      </div>
    )
  }
}

MyDeliveries.propTypes = {
  isFetchingOrders: PropTypes.bool,
  didErrorFetchingPendingOrders: PropTypes.shape({ message: PropTypes.string }),
  didErrorFetchingProjectedOrders: PropTypes.shape({ message: PropTypes.string }),
  userLoadData: PropTypes.func.isRequired,
  userLoadNewOrders: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
}

MyDeliveries.defaultProps = {
  isFetchingOrders: false,
  didErrorFetchingPendingOrders: null,
  didErrorFetchingProjectedOrders: null,
}

export default MyDeliveries
